import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {
    RulesetDto,
    fromTimestamp,
    toTimestamp
} from '@flyfreely-portal-ui/flyfreely';
import moment from 'moment';
import {
    Observable,
    Subject,
    debounceTime,
    distinctUntilChanged,
    map,
    takeUntil
} from 'rxjs';
import { MapAirspaceCheckerService } from '../map-airspace-checker/map-airspace-checker.service';

interface AirspaceCheckerParametersForm {
    airspaceDate: FormControl<Date>;
    airspaceDuration: FormControl<number>;
    airspaceRuleset: FormControl<RulesetDto>;
    airspaceHeight: FormControl<number>;
    airspaceTimeZone: FormControl<string>;
    radius: FormControl<number>;
}

const EMPTY_LIST = [];

function isInFutureValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;

        // Allow a 1 minute buffer to ensure the time doesn't immediately invalidate when running the check
        const isInPast = moment(value).isBefore(moment(), 'minutes');

        // return isInPast ? { isInPast: true } : null;
        return null;
    };
}

/**
 * The parameter panel for the airspace checker.
 *
 * In this panel, all dates are in the local time of the airspace checker's
 * timezone, and is converted back to absolute time when saved.
 */
@Component({
    selector: 'map-airspace-parameters',
    templateUrl: './map-airspace-parameters.component.html',
    styleUrls: ['./map-airspace-parameters.component.scss']
})
export class MapAirspaceParametersComponent implements OnInit, OnDestroy {
    @Input() autoSave = false;
    @Output() doneEditing = new EventEmitter<void>();

    formGroup = new FormGroup({
        airspaceDate: new FormControl<Date>(undefined, [isInFutureValidator()]),
        // The airspace checker can't check 24 hours or beyond, so it's enforced via form validators
        airspaceDuration: new FormControl<number>(undefined, [
            Validators.max(82800),
            Validators.min(1)
        ]),
        airspaceRuleset: new FormControl<RulesetDto>(
            undefined,
            Validators.required
        ),
        airspaceHeight: new FormControl<number>(undefined, Validators.required),
        radius: new FormControl<number>(undefined)
    });

    working = false;

    airspaceTimeZone?: string;
    // This is set if the the set now is clicked
    forceAutomaticTime = false;

    airspaceRulesetList: RulesetDto[] | undefined;

    timeZoneAbbreviation$: Observable<string>;
    radius = false; // FIXME

    private ngUnsubscribe$ = new Subject<void>();

    constructor(private mapAirspaceCheckerService: MapAirspaceCheckerService) {
        this.mapAirspaceCheckerService.airspaceJurisdiction$
            .pipe(
                distinctUntilChanged(),
                map(j => j?.rulesetList ?? EMPTY_LIST),
                map(l =>
                    l.filter(
                        r => r.identifier != null && r.identifier.length > 0
                    )
                ),
                takeUntilDestroyed()
            )
            .subscribe(rulesetList => (this.airspaceRulesetList = rulesetList));

        this.timeZoneAbbreviation$ =
            this.mapAirspaceCheckerService.currentParameters$.pipe(
                map(p => p?.airspaceTimeZone),
                distinctUntilChanged(),
                map(tz => (tz != null ? `${moment().tz(tz).format('z')}` : ''))
            );

        this.mapAirspaceCheckerService.currentParameters$
            .pipe(takeUntilDestroyed())
            .subscribe(p => {
                const localisedTime = fromTimestamp(
                    p.airspaceDate ?? new Date(),
                    p.airspaceTimeZone
                );
                this.airspaceTimeZone = p.airspaceTimeZone;

                if (
                    p.airspaceRuleset != null &&
                    this.airspaceRulesetList != null
                ) {
                    const airspaceRuleset = this.airspaceRulesetList.find(
                        r => r.id === p.airspaceRuleset.id
                    );
                    this.formGroup.patchValue(
                        {
                            ...p,
                            airspaceRuleset,
                            airspaceDate: localisedTime
                        },
                        { emitEvent: false }
                    );
                } else {
                    this.formGroup.patchValue(
                        {
                            ...p,
                            airspaceDate: localisedTime
                        },
                        { emitEvent: false }
                    );
                }
                this.formGroup.controls.airspaceRuleset.updateValueAndValidity({
                    onlySelf: true
                });
                this.formGroup.controls.airspaceHeight.updateValueAndValidity({
                    onlySelf: true
                });
            });
    }

    ngOnInit() {
        if (this.autoSave) {
            this.formGroup.valueChanges
                .pipe(
                    debounceTime(500),
                    distinctUntilChanged(
                        (a, b) =>
                            a.airspaceHeight === b.airspaceHeight &&
                            a.airspaceDuration === b.airspaceDuration &&
                            a.airspaceRuleset?.id === b.airspaceRuleset?.id &&
                            (a.airspaceDate?.getTime() ?? null) ===
                                (b.airspaceDate?.getTime() ?? null)
                    ),
                    takeUntil(this.ngUnsubscribe$)
                )
                .subscribe(() => {
                    const manualTime =
                        this.formGroup.controls.airspaceDate.dirty;
                    const values = this.formGroup.value;
                    this.mapAirspaceCheckerService.setParameters(
                        {
                            ...values,
                            airspaceDate: new Date(
                                toTimestamp(
                                    values.airspaceDate,
                                    this.airspaceTimeZone
                                )
                            )
                        },
                        manualTime
                            ? false
                            : this.forceAutomaticTime
                            ? true
                            : undefined
                    );
                    this.forceAutomaticTime = false;
                });
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    setTimeToNow() {
        this.forceAutomaticTime = true;
        this.formGroup.patchValue({
            airspaceDate: fromTimestamp(new Date(), this.airspaceTimeZone)
        });
        this.formGroup.controls.airspaceDate.markAsPristine();
        this.formGroup.updateValueAndValidity();
    }

    update() {
        const manualTime = this.formGroup.controls.airspaceDate.dirty;
        const values = this.formGroup.value;
        this.mapAirspaceCheckerService.setParameters(
            {
                ...values,
                airspaceDate: new Date(
                    toTimestamp(values.airspaceDate, this.airspaceTimeZone)
                )
            },
            manualTime ? false : this.forceAutomaticTime ? true : undefined
        );
        this.doneEditing.emit();
    }
}
